import { NavLink, Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AlertBadge from '../alerts/AlertBadge';
import useWebSocket from '../../api/websocket';
import { useEffect, useState, Fragment } from 'react';
import RadioPlayer from '../dashboard/RadioPlayer';
import StatusBar from './StatusBar';
import QueuePanel from '../dashboard/QueuePanel';

const navigation = [
  { name: 'Panel de control', to: '/dashboard', icon: '📊' },
  { name: 'Programación semanal', to: '/programacion', icon: '🗓️' },
  { name: 'Series', to: '/shows', icon: '📺' },
  { name: 'DJs', to: '/djs', icon: '🎧' },
  {
    name: 'Música',
    icon: '🎼',
    children: [
      { name: 'Carpetas', to: '/music/folders' },
      { name: 'Duplicados', to: '/music/duplicates' },
      { name: 'Rotaciones', to: '/rotations' },
      { name: 'Fallback', to: '/history/fallback' },
    ],
  },
  { name: 'Biblioteca de medios', to: '/media', icon: '🎵' },
  { name: 'Playlists', to: '/playlists', icon: '📜' },
  { name: 'Bloques de Rotación', to: '/rotation-blocks', icon: '🔀' },
  {
    name: 'Publicidad',
    icon: '📢',
    children: [
      { name: 'Patrocinadores', to: '/advertising/advertisers' },
      { name: 'Campañas', to: '/advertising/campaigns' },
      { name: 'Spots', to: '/advertising/spots' },
      { name: 'Tandas', to: '/advertising/breaks' },
      { name: 'Broadcasts', to: '/advertising/broadcasts' },
    ],
  },
  { name: 'Transmisión', to: '/streaming', icon: '📡' },
  {
    name: 'Activos de audio',
    icon: '🔊',
    children: [
      { name: 'Jingles', to: '/jingles' },
      { name: 'Promos', to: '/promos' },
    ],
  },
  { name: 'Usuarios', to: '/users', icon: '👤' },
  { name: 'Configuración', to: '/settings', icon: '⚙️' },
  { name: 'Alertas', to: '/alerts', icon: '🔔' },
  { name: 'Estadísticas', to: '/stats', icon: '📊' },
  {
    name: 'Historial',
    icon: '📚',
    children: [
      { name: 'Emisión', to: '/history/broadcast' },
      { name: 'Música', to: '/history/music' },
      { name: 'Publicidad', to: '/history/ads' },
      { name: 'Fallback', to: '/history/fallback' },
    ],
  },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const [nowPlaying, setNowPlaying] = useState(null);

  const { subscribe } = useWebSocket((data) => {
    if (data.type === 'now_playing') {
      setNowPlaying(data.payload);
    }
  });

  useEffect(() => {
    const unsub = subscribe('now_playing', (data) => setNowPlaying(data.payload));
    return unsub;
  }, [subscribe]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="flex h-screen overflow-hidden bg-dark-900 text-dark-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={closeSidebar}></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 overflow-y-auto bg-dark-850 border-r border-dark-700 transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center justify-between px-4 border-b border-dark-700">
          <h1 className="text-xl font-bold text-primary-400">AUTODJ RADIO</h1>
          <button onClick={closeSidebar} className="lg:hidden text-dark-400 hover:text-dark-200">
            ✕
          </button>
        </div>

        <nav className="py-2">
          {navigation.map((item) => {
            if (item.children) {
              const isChildActive = item.children.some((child) => location.pathname.startsWith(child.to));
              return (
                <div key={item.name}>
                  <details open={isChildActive} className="group">
                    <summary className="flex cursor-pointer items-center gap-3 px-4 py-2 text-sm font-medium text-dark-200 hover:bg-dark-800 hover:text-dark-100">
                      <span>{item.icon}</span>
                      <span>{item.name}</span>
                      <span className="ml-auto">&#9654;</span>
                    </summary>
                    <div className="ml-6 border-l border-dark-700">
                      {item.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          onClick={closeSidebar}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-4 py-2 text-sm ${
                              isActive
                                ? 'bg-primary-900/20 text-primary-400 border-l-2 border-primary-500'
                                : 'text-dark-400 hover:bg-dark-800 hover:text-dark-200'
                            }`
                          }
                        >
                          <span>{child.name}</span>
                        </NavLink>
                      ))}
                    </div>
                  </details>
                </div>
              );
            }

            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 text-sm font-medium ${
                    isActive
                      ? 'bg-primary-900/20 text-primary-400 border-l-2 border-primary-500'
                      : 'text-dark-300 hover:bg-dark-800 hover:text-dark-100'
                  }`
                }
              >
                <span className="w-5 text-center">{item.icon}</span>
                <span>{item.name}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-dark-700 p-4">
          <div className="flex items-center gap-3">
            <img
              src={user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'DJ')}&background=7c3aed&color=fff`}
              alt={user?.name}
              className="h-10 w-10 rounded-full"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-dark-100">{user?.name}</p>
              <p className="text-xs text-dark-400">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="mt-4 w-full text-left text-sm text-dark-400 hover:text-dark-200"
          >
            Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden lg:ml-64">
        {/* Header */}
        <header className="flex h-16 items-center justify-between bg-dark-850 border-b border-dark-700 px-4">
          <div className="flex items-center gap-4">
            <button
              onClick={toggleSidebar}
              className="rounded-md p-2 text-dark-400 hover:bg-dark-800 hover:text-dark-200 lg:hidden"
            >
              ☰
            </button>
            <h2 className="text-lg font-semibold text-dark-100">
              {navigation.find((n) => location.pathname.startsWith(n.to))?.name || 'AUTODJ RADIO'}
            </h2>

            {/* Now Playing in header */}
            {nowPlaying && (
              <div className="hidden items-center gap-3 rounded-md bg-dark-800 px-3 py-1.5 text-sm md:flex">
                <span className="text-success-400">●</span>
                <span className="max-w-[220px] truncate font-medium text-dark-100">{nowPlaying.artist || 'Unknown'} - {nowPlaying.title || 'Unknown'}</span>
                <span className="text-dark-500">({nowPlaying.program_name || 'AUTODJ RADIO'})</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setQueueOpen(true)}
              className="relative rounded-md px-3 py-1.5 text-sm font-medium text-dark-200 hover:bg-dark-800"
              title="Ver / agregar a la cola de Próximos (podés arrastrar canciones desde la Biblioteca)"
            >
              🎵 Próximos
              {queueCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] font-bold text-white">
                  {queueCount}
                </span>
              )}
            </button>
            <RadioPlayer compact />
            <Link to="/alerts" className="relative text-dark-400 hover:text-warning-400">
              🔔
              <AlertBadge />
            </Link>
          </div>
         </header>

        <StatusBar canControl={user?.role === 'admin' || user?.role === 'operator'} />

        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>

      <QueuePanel open={queueOpen} onClose={() => setQueueOpen(false)} onCount={setQueueCount} />
    </div>
  );
};

export default AdminLayout;
